import { reactive, toRefs } from "vue";
import axiosInstance from "axios";
import applyCaseMiddleware from "axios-case-converter";
import { BASE_URL } from "@/constants/env";
import { useAuthentication } from "@/composables/useAuthentication";
import { ROUTE_PAGE_NOT_FOUND } from "@/constants/routes";
import router from "@/router";

const axiosData = reactive({
  axios: applyCaseMiddleware(
    axiosInstance.create({
      baseURL: BASE_URL,
      headers: {
        "Cache-Control": "no-cache",
        Accept: "application/json",
        Pragma: "no-cache",
        Expires: "0",
        "X-User-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    })
  ),
});

/**
 * Axios errors carry this flag when the request was rejected for exceeding the
 * server's upload size limit. See detectPayloadTooLarge below.
 */
export interface PayloadTooLargeError {
  isPayloadTooLarge?: boolean;
}

/**
 * Decide whether a failed request was rejected for being too large.
 *
 * The obvious case is a 413. The non-obvious one is that nginx generates that
 * 413 itself, before the app's CORS middleware ever runs, so the response has
 * no `Access-Control-Allow-Origin` header — the browser then refuses to expose
 * it to JS and axios reports a bare network error with no `response` at all.
 * Treating a response-less failure on a multipart request as the same
 * condition is what turns "please try again" into an actionable message.
 */
const detectPayloadTooLarge = (error: unknown): boolean => {
  const err = error as {
    response?: { status?: number };
    code?: string;
    config?: { data?: unknown; headers?: Record<string, unknown> };
  };

  if (err?.response) return err.response.status === 413;
  if (err?.code !== "ERR_NETWORK") return false;

  const headers = err?.config?.headers ?? {};
  const contentType = String(headers["Content-Type"] ?? headers["content-type"] ?? "");

  return contentType.includes("multipart/form-data") || (typeof FormData !== "undefined" && err?.config?.data instanceof FormData);
};

export function useAxios() {
  const { signOut } = useAuthentication();
  axiosData.axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response) {
        const { status } = error.response;

        if (status === 401) signOut();
        if (status === 403) router.replace(ROUTE_PAGE_NOT_FOUND);
      }

      // Tagged rather than handled globally: the right message depends on what
      // was being uploaded, so the calling view decides what to show.
      if (detectPayloadTooLarge(error)) (error as PayloadTooLargeError).isPayloadTooLarge = true;

      // Todo: handle every error

      return Promise.reject(error);
    }
  );

  const setAdminHeader = (isAdminPrefix = false) => {
    if (isAdminPrefix) axiosData.axios.defaults.headers.admin = isAdminPrefix;
  };

  const setAdvisorHeader = (isAdvisorPrefix = false) => {
    if (isAdvisorPrefix) axiosData.axios.defaults.headers.advisor = isAdvisorPrefix;
  };

  const setToken = (token: string) => {
    axiosData.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  };

  const removeToken = () => {
    axiosData.axios.defaults.headers.common.Authorization = "";
  };

  return {
    setToken,
    removeToken,
    setAdminHeader,
    setAdvisorHeader,
    ...toRefs(axiosData),
  };
}
